import type { HubBandTone, HubRowSafety } from "@/lib/conditions/hub/types";
import motion from "./hub-motion.module.css";

/**
 * Small presentational pieces shared by the hub board and search (F4): the GO score ring, the
 * band pill and the safety pill. No hooks, so server and client components can both use them.
 */

const RING_CIRCUMFERENCE = 163.36;

/** Arc colour by GO score band (85+, 70 to 84, 55 to 69, under 55). */
export function hubRingColour(score100: number): string {
  if (score100 >= 85) return "#0E9E7E";
  if (score100 >= 70) return "#C79A3A";
  if (score100 >= 55) return "#C17B5D";
  return "#5C6472";
}

/** The stroke dash array for a score out of 100 on the r26 ring. */
export function hubRingDash(score100: number): string {
  const clamped = Math.max(0, Math.min(100, score100));
  const arc = Math.round((clamped / 100) * RING_CIRCUMFERENCE * 100) / 100;
  const rest = Math.round((RING_CIRCUMFERENCE - arc) * 100) / 100;
  return `${arc.toFixed(2)} ${rest > 0 ? rest.toFixed(2) : "0"}`;
}

/**
 * A decorative GO score ring; the row's accessible name carries the score. Size it with `size`
 * (pixels) or with `className` (responsive size classes, for example "size-12 md:size-[54px]");
 * the number size is set by `numberClassName`.
 */
export function HubScoreRing({
  score100,
  size = 54,
  className,
  strokeWidth = 6,
  numberClassName = "text-[18px]",
  animate = false,
  delayMs = 0,
}: Readonly<{
  score100: number;
  size?: number;
  className?: string;
  strokeWidth?: number;
  numberClassName?: string;
  animate?: boolean;
  delayMs?: number;
}>) {
  const arcColour = score100 > 0 ? hubRingColour(score100) : "#EDF1EC";
  return (
    <span
      aria-hidden="true"
      className={`relative inline-flex shrink-0 ${className ?? ""}`}
      style={className ? undefined : { width: size, height: size }}
      data-hub-ring={score100}
    >
      <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false" className="size-full">
        <circle cx="32" cy="32" r="26" fill="none" stroke="#EDF1EC" strokeWidth={strokeWidth} />
        <circle
          className={animate ? motion.ring : undefined}
          cx="32"
          cy="32"
          r="26"
          fill="none"
          stroke={arcColour}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={hubRingDash(score100)}
          transform="rotate(-90 32 32)"
          style={animate && delayMs > 0 ? { animationDelay: `${delayMs}ms` } : undefined}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center font-bold text-navy ${numberClassName}`}>
        {score100}
      </span>
    </span>
  );
}

const BAND_TONE_CLASS: Readonly<Record<HubBandTone, string>> = {
  hero: "bg-[#E3F4EF] text-[#0A6E58]",
  firing: "bg-[#F6EEDC] text-[#7C5A1B]",
  good: "bg-[#F5E8E2] text-[#8E4C30]",
  other: "bg-[#EDEFF2] text-[#454C5A]",
};

/** The band pill under a spot name ("Firing", "Hero", "Good"). */
export function HubBandPill({ label, tone }: Readonly<{ label: string; tone: HubBandTone }>) {
  return (
    <span className={`inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full px-[9px] text-[12px] font-bold ${BAND_TONE_CLASS[tone]}`}>
      {label}
    </span>
  );
}

/** The words each safety state shows. */
export const HUB_SAFETY_LABEL: Readonly<Record<Exclude<HubRowSafety, null>, string>> = {
  red: "Red safety flag",
  amber: "Caution",
  unavailable: "Safety evidence unavailable",
};

const SAFETY_CLASS: Readonly<Record<Exclude<HubRowSafety, null>, string>> = {
  red: "border-[#F0B4B4] bg-[#FDECEC] text-[#8E1F1F]",
  amber: "border-[#E8C766] bg-[#FFF6DB] text-[#6B4E00]",
  unavailable: "border-[#D5DBD2] bg-warm-bg text-[#454C5A]",
};

/** The safety pill on a row: red, amber, or no evidence (beta boards). Nothing for null. */
export function HubSafetyPill({ safety }: Readonly<{ safety: HubRowSafety }>) {
  if (!safety) return null;
  return (
    <span
      className={`inline-flex h-[22px] shrink-0 items-center gap-[5px] whitespace-nowrap rounded-full border px-[9px] text-[12px] font-semibold ${SAFETY_CLASS[safety]}`}
      data-hub-safety={safety}
    >
      {HUB_SAFETY_LABEL[safety]}
    </span>
  );
}
